import { useEffect, useMemo } from 'react'

import { useToast } from '@ui/toasts/useToastManager'
import { zodResolver } from '@hookform/resolvers/zod'
import { trpc } from 'api'
import useCustomerInvoice from 'hooks/useCustomerInvoice'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { InvoiceFormSchema } from 'utils/vatSchema'

export const getSaveDate = (date?: Date | string | null) => {
  if (!date) return null
  if (typeof date === 'string') return date
  return date.toISOString().split('T')[0]
}

const useInvoiceForm = ({
  open,
  close,
  onSuccess,
}: {
  open: boolean
  close: () => void
  onSuccess?: () => void
}) => {
  const { data: invoice } = useCustomerInvoice()

  const defaultValues = useMemo(
    () => ({
      firstName: invoice?.firstName || '',
      lastName: invoice?.lastName || '',
      vatId: invoice?.vat || '',
      street: invoice?.street || '',
      street2: invoice?.street2 || '',
      birth_date: invoice?.birth_date || null,
      city: invoice?.city || '',
      zip: invoice?.zip || '',
      email: invoice?.email || '',
      mobile: invoice?.mobile || '',
      country_code: invoice?.country_code || '',
      type: 'invoice',
    }),
    [invoice]
  )

  const { t } = useTranslation(['toasts'])
  const form = useForm<typeof defaultValues, any, typeof defaultValues>({
    defaultValues,
    // react-hook-form ≥7.55: zodResolver infers z.input<schema> as TFieldValues, mismatching
    // typeof defaultValues (e.g. birth_date: string|null vs unknown, type: string vs "invoice").
    // `as any` preserves pre-7.55 behavior — Zod still validates at runtime.
    // See docs/expo54-migration.md § Known Issues.
    resolver: zodResolver(InvoiceFormSchema) as any,
  })

  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues])

  const utils = trpc.useUtils()

  const toast = useToast()

  const patch = trpc.customer.address.patch.useMutation({
    onSuccess: (res) => {
      utils.customer.address.list.invalidate()
      onSuccess?.()
      onClose()
    },
    onError: (err) => {
      console.error(err)
      toast({
        variant: 'error',
        title: t('toasts:error.invoice.patch.title'),
        text: err.message,
      })
    },
  })

  const create = trpc.customer.address.create.useMutation({
    onSuccess: () => {
      utils.customer.address.list.invalidate()
      onSuccess?.()
      onClose()
    },
    onError: (err) => {
      console.error(err)
      toast({
        variant: 'error',
        title: t('toasts:error.invoice.create.title'),
        text: err.message,
      })
    },
  })

  const onSubmit = form.handleSubmit(
    (values) => {
      const state = form.formState
      const hasDirtyFields =
        Object.values(state.dirtyFields).filter(Boolean).length > 0

      if (!hasDirtyFields) {
        onSuccess?.()
        onClose()
        return
      }

      if (!invoice?.id) {
        create.mutate({
          ...values,
          birth_date: getSaveDate(values.birth_date),
          type: 'invoice',
        })
        return
      }
      patch.mutate({
        id: invoice?.id,
        ...values,
        birth_date: getSaveDate(values.birth_date),
        type: 'invoice',
      })
    },
    (err) => {
      console.log(err)
    }
  )

  const onClose = () => {
    form.reset()

    close()
  }

  useEffect(() => {
    if (open) {
      form.reset(defaultValues)
    }
  }, [open])

  return {
    onSubmit,
    create,
    patch,
    form,
    onClose,
    invoice,
  }
}

export default useInvoiceForm
